/** biome-ignore-all lint/style/useNamingConvention: Optimizely */
/** biome-ignore-all lint/nursery/noTsIgnore: This needs to be revisited */
/** biome-ignore-all lint/nursery/noExcessiveLinesPerFunction: This needs to be revisited */

import type { Client, OptimizelyUserContext } from '@optimizely/optimizely-sdk';
import Cookies from 'js-cookie';
import { pushToDataLayer } from '@/app_lib/services/Gtm/utils';
import type { PageAdConfig } from '@/app_lib/types/Ad';
import { getOptimizelyAttributes } from '@/main/lib/providers/OptimizelyProvider/attributes';
import { getAnonUser } from '@/main/lib/providers/OptimizelyProvider/utils';

const cumulativeMetrics = new Set(['total_session_view_time']);

const primaryTrackingMetrics = [
  'leaderboard_ad',
  'scroll_depth',
  'time_on_page',
  'saw_first_ad',
  'saw_second_ad',
  'saw_third_ad',
  'saw_fourth_ad',
  'saw_fifth_ad',
  'saw_static_right_rail',
  'saw_sticky_right_rail',
  'finished_article',
  'saw_outbrain',
  'saw_stn_video',
  'ad_impressions',
  'return_visits_via_utm',
  'total_session_view_time',
];

let _cachedUser: OptimizelyUserContext | null = null;
let _visibilityBeaconSent = false;

export function invalidateCachedUser() {
  if (_cachedUser && (_cachedUser as any).__visibilityHandler) {
    document.removeEventListener('visibilitychange', (_cachedUser as any).__visibilityHandler);
  }
  _cachedUser = null;
  _visibilityBeaconSent = false;
}

export const getUserContext = async (optimizely: Client, pageAdConfig?: PageAdConfig) => {
  if (_cachedUser) {
    return _cachedUser;
  }
  const attributes = await getOptimizelyAttributes(pageAdConfig);
  const userId = Cookies.get('pianoUid') || getAnonUser();
  const user = optimizely?.createUserContext(userId, attributes) ?? null;
  if (!user) {
    return null;
  }

  // initialize timestamps used to compute active engagement time (milliseconds)
  try {
    const now = Date.now();
    const visible = typeof document !== 'undefined' ? document.visibilityState === 'visible' : true;
    // @ts-ignore - attach internal fields to user object
    (user as any).__lastTimeOnPageSentAt = now;
    (user as any).__lastActiveAt = visible ? now : 0;

    const visibilityHandler = () => {
      try {
        // if it becomes visible, mark last active
        if (document.visibilityState === 'visible') {
          (user as any).__lastActiveAt = Date.now();
          return;
        }

        // when it becomes hidden (or not visible), mark inactive
        (user as any).__lastActiveAt = 0;

        // send a single summary beacon the first time page becomes hidden
        if (!_visibilityBeaconSent) {
          _visibilityBeaconSent = true;
          // Build test/variant summary using current tests available on page
          const metrics: Record<string, number> = {};
          let hasMetrics = false;
          for (const metric of primaryTrackingMetrics) {
            try {
              const metricValue = sessionStorage.getItem(metric);
              if (metricValue) {
                metrics[`opt_${metric}`] = Number(metricValue);
                hasMetrics = true;
                if (!cumulativeMetrics.has(metric)) {
                  sessionStorage.removeItem(metric);
                }
              }
            } catch {}
          }

          const payload = {
            event: 'optimizely_tracking_event',
            opt_test_info: '',
            path: typeof location !== 'undefined' ? location.pathname : '',
            ...metrics,
          };

          try {
            let testInfo = '{';
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const allTests: any[] = (globalThis as any).__optimizely_current_tests || [];
            // if not populated, fallback to currentTests import by requiring module-level var (no-op on client)
            if (Array.isArray(allTests) && allTests.length > 0) {
              for (const t of allTests) {
                try {
                  // attempt to decide on each known test using the user object
                  if (t?.testName && (user as any).decide) {
                    const decision = (user as any).decide(t.testName);
                    if (decision?.enabled) {
                      testInfo += ` ${t.testName} : ${decision.variationKey}, `;
                    }
                  }
                } catch {}
              }
            }
            testInfo += 'opt: true}';
            payload.opt_test_info = testInfo;
            if (hasMetrics) {
              pushToDataLayer(payload);
            }
          } catch {}
        }
      } catch {}
    };

    // avoid duplicate handlers on same user object
    if (
      typeof document !== 'undefined' &&
      typeof document.addEventListener === 'function' &&
      !(user as any).__visibilityHandler
    ) {
      document.addEventListener('visibilitychange', visibilityHandler);
      // @ts-ignore
      (user as any).__visibilityHandler = visibilityHandler;
    }

    if ((user as any).trackEvent) {
      const originalTrack = (user as any).trackEvent.bind(user);
      // @ts-ignore
      (user as any).trackEvent = async (eventKey: string, eventTags?: Record<string, any>) => {
        try {
          const nowEvent = Date.now();
          const lastSent = (user as any).__lastTimeOnPageSentAt || nowEvent;
          const wasActive = (user as any).__lastActiveAt && (user as any).__lastActiveAt > 0;
          const activeDuration = wasActive && document.visibilityState === 'visible' ? nowEvent - lastSent : 0;

          // @ts-ignore
          await originalTrack(eventKey, eventTags);
          if (activeDuration > 0) {
            try {
              // @ts-ignore
              await originalTrack('time_on_page', {
                $opt_event_properties: { from_event: eventKey },
                value: activeDuration / 1000, // convert ms to seconds
              });
              // add time_on_page to session storage
              const currentTimeOnPage = sessionStorage.getItem('time_on_page');
              const newTimeOnPage = currentTimeOnPage
                ? Number(currentTimeOnPage) + Math.round(activeDuration / 1000)
                : Math.round(activeDuration / 1000);
              sessionStorage.setItem('time_on_page', newTimeOnPage.toString());
              const currentTotal = sessionStorage.getItem('total_session_view_time');
              const newTotal = currentTotal
                ? Number(currentTotal) + Math.round(activeDuration / 1000)
                : Math.round(activeDuration / 1000);
              sessionStorage.setItem('total_session_view_time', newTotal.toString());
            } catch {
              // ignore errors sending time_on_page
            }
          }

          // update the lastTimeOnPageSentAt timestamp regardless so interval won't duplicate
          (user as any).__lastTimeOnPageSentAt = nowEvent;
          if (document.visibilityState === 'visible') {
            (user as any).__lastActiveAt = nowEvent;
          }
        } catch {}
      };
    }
  } catch {
    // ignore any errors on attaching handlers
  }

  _cachedUser = user;
  return _cachedUser;
};

export const trackOptimizelyEvent = async (
  optimizely: Client,
  event: string,
  properties: Record<string, string | number>,
) => {
  try {
    const user = await getUserContext(optimizely, window?.arcSpanPageConfig);
    if (!user) {
      return;
    }

    const { value, ...rest } = properties ?? {};
    const tags = {
      $opt_event_properties: rest || {},
      value: value || 0,
    };
    // @ts-ignore

    user.trackEvent(event, tags);
    if (event === 'ad_impressions') {
      return;
    }
    if ((event.includes('ad') || event.includes('signed_up')) && !event.includes('_loadtime')) {
      try {
        const currentAdImpressions = sessionStorage.getItem('ad_impressions');
        const newAdImpressions = currentAdImpressions ? Number(currentAdImpressions) + 1 : 1;
        sessionStorage.setItem('ad_impressions', newAdImpressions.toString());
      } catch {}
    }
    sessionStorage.setItem(event, '1');
  } catch {}
};
